import { loadProperty, type DataLoaderConfig } from '@vibesites/runtime';
import type { CompanyData } from '@vibesites/runtime';
import { propertySummaries } from '../vibesites-company-data';
import {
  communitySearchIndexById,
  communitySearchIndexBySlug,
  type CommunitySearchIndexEntry,
} from './communitySearchIndex';
import {
  communityBedroomAvailabilityById,
  communityBedroomAvailabilityBySlug,
} from './communityBedroomAvailability';

export const COMMUNITY_BASE_PATH = '/our-communities';

export type CommunityProperty = Record<string, any>;

export function slugify(str: string | null | undefined): string {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

/** Strip HTML tags from richtext token values used as plain attributes/props. */
export function plainText(html: string | null | undefined): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .trim();
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

export function getStateName(abbr: string): string {
  return STATE_NAMES[abbr?.toUpperCase()] || abbr;
}

export function normalizeAbbr(val: string): string {
  if (!val) return '';
  if (val.length <= 2) return val.toUpperCase();
  const upper = val.toUpperCase();
  for (const [abbr, name] of Object.entries(STATE_NAMES)) {
    if (name.toUpperCase() === upper) return abbr;
  }
  return val;
}

export function withCommunitySlugs(prop: CommunityProperty): CommunityProperty {
  const abbr = normalizeAbbr(prop.property_address_state || '');
  const stateSlug = prop._stateSlug && /^[a-z]{2}$/.test(prop._stateSlug)
    ? prop._stateSlug
    : slugify(abbr);
  const citySlug = prop._citySlug || slugify(prop.property_address_city);
  const propertySlug = prop._slug || slugify(prop.property_slug || prop.property_name);
  return { ...prop, _slug: propertySlug, _stateSlug: stateSlug, _citySlug: citySlug };
}

export function getCommunityPath(property: CommunityProperty): string {
  const prop = withCommunitySlugs(property);
  return `${COMMUNITY_BASE_PATH}/${prop._stateSlug}/${prop._citySlug}/${prop._slug}`;
}

/** Drop incomplete / non-community rows (e.g. office entities without listing data). */
function isCommunityListing(prop: CommunityProperty): boolean {
  const hasListingSignal = Boolean(
    prop.property_featured_image ||
      prop.property_price_min ||
      prop.property_price_range ||
      prop.property_total_units ||
      prop.property_bedrooms_min,
  );
  return hasListingSignal;
}

export function getCommunityProperties(companyData?: CompanyData | null): CommunityProperty[] {
  const live = companyData?.properties;
  const source = live && live.length > 0 ? live : propertySummaries;
  return source
    .map((prop) => withCommunitySlugs(prop as CommunityProperty))
    .filter(isCommunityListing);
}

export async function fetchPropertyDetail(
  slug: string,
  propertyId?: string,
): Promise<CommunityProperty | null> {
  const apiToken = typeof window !== 'undefined' ? window.localStorage.getItem('vibesites-api-token') : null;
  const apiUrl = (import.meta as { env?: { MULTIHUB_API_URL?: string } }).env?.MULTIHUB_API_URL;

  if (apiToken && apiUrl && propertyId) {
    try {
      const config: DataLoaderConfig = { endpoint: apiUrl, token: apiToken, propertyId };
      return await loadProperty(config);
    } catch {
      // Fall through to static JSON.
    }
  }

  return fetch(`/data/properties/${slug}.json`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

/** Display phone as NNN-NNN-NNNN for compact CTA pills. */
export function formatPhoneCompact(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const ten =
    digits.length === 11 && digits[0] === '1'
      ? digits.slice(1)
      : digits.length === 10
        ? digits
        : '';
  if (!ten) return phone;
  return `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export function phoneHref(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  return `tel:+${digits.length === 10 ? `1${digits}` : digits}`;
}

export function formatCurrency(amount: number | string | null | undefined): string {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (n == null || Number.isNaN(n)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function getFullAddress(prop: CommunityProperty): string {
  if (prop.property_full_address) return String(prop.property_full_address);
  const street = prop.property_address_1 || prop.property_address || '';
  const city = prop.property_address_city || '';
  const state = prop.property_address_state || '';
  const zip = prop.property_address_zip || '';
  const locality = [city, state].filter(Boolean).join(', ');
  const localityZip = [locality, zip].filter(Boolean).join(locality && zip ? ' ' : '');
  return [street, localityZip].filter(Boolean).join(', ');
}

function bedLabel(raw: string): string {
  const v = raw.toLowerCase().trim();
  if (v === 'studio' || v === '0') return 'Studio';
  const n = Number(v);
  if (!Number.isNaN(n)) {
    if (n === 0) return 'Studio';
    if (n === 1) return 'One Bedroom';
    if (n === 2) return 'Two Bedroom';
    if (n === 3) return 'Three Bedroom';
    if (n === 4) return 'Four Bedroom';
    return `${n} Bedroom`;
  }
  return raw;
}

function expandBedTypes(minRaw: string, maxRaw: string): string[] {
  const parse = (v: string) => {
    const lower = v.toLowerCase().trim();
    if (lower === 'studio') return 0;
    const n = Number(lower);
    return Number.isNaN(n) ? null : n;
  };
  const min = parse(minRaw);
  const max = parse(maxRaw || minRaw);
  if (min == null) return minRaw ? [minRaw] : [];
  const end = max == null ? min : max;
  const out: string[] = [];
  for (let i = min; i <= end; i += 1) out.push(String(i));
  return out;
}

export interface BedroomPriceLine {
  label: string;
  price: string;
}

/** Best-effort bedroom starting prices from summary price/bed ranges. */
export function getBedroomPriceLines(prop: CommunityProperty): BedroomPriceLine[] {
  const beds = expandBedTypes(
    String(prop.property_bedrooms_min || ''),
    String(prop.property_bedrooms_max || prop.property_bedrooms_min || ''),
  );
  const priceMin = Number(prop.property_price_min);
  const priceMax = Number(prop.property_price_max);
  if (!beds.length || Number.isNaN(priceMin)) return [];

  if (beds.length === 1) {
    return [{ label: bedLabel(beds[0]), price: formatCurrency(priceMin) }];
  }

  return beds.map((bed, index) => {
    const t = beds.length === 1 ? 0 : index / (beds.length - 1);
    const price =
      Number.isNaN(priceMax) || priceMax <= priceMin
        ? priceMin
        : Math.round(priceMin + (priceMax - priceMin) * t);
    return { label: bedLabel(bed), price: formatCurrency(price) };
  });
}

export type BedroomAvailabilityStatus = 'available' | 'limited' | 'unavailable' | 'unknown';

export interface BedroomAvailabilityLine {
  label: string;
  status: BedroomAvailabilityStatus;
  statusLabel: string;
}

const LIMITED_AVAILABILITY_MAX_UNITS = 3;

const AVAILABILITY_STATUS_LABELS: Record<BedroomAvailabilityStatus, string> = {
  available: 'Available',
  limited: 'Limited Availability',
  unavailable: 'Not Available',
  unknown: 'Call for Availability',
};

function shortBedLabel(bed: number): string {
  return bed === 0 ? 'Studio' : `${bed} BD`;
}

/** One row per bedroom type, with availability from baked floorplan unit counts. */
export function getBedroomAvailabilityLines(prop: CommunityProperty): BedroomAvailabilityLine[] {
  const id = String(prop.multihub_property_id || '');
  const slug = String(prop._slug || prop.property_slug || '');
  const entry =
    (id && communityBedroomAvailabilityById.get(id)) ||
    (slug && communityBedroomAvailabilityBySlug.get(slug)) ||
    undefined;

  if (entry) {
    return Object.entries(entry.beds)
      .map(([bed, units]) => ({ bed: Number(bed), units }))
      .sort((a, b) => a.bed - b.bed)
      .map(({ bed, units }) => {
        const status: BedroomAvailabilityStatus =
          units <= 0 ? 'unavailable' : units <= LIMITED_AVAILABILITY_MAX_UNITS ? 'limited' : 'available';
        return { label: shortBedLabel(bed), status, statusLabel: AVAILABILITY_STATUS_LABELS[status] };
      });
  }

  const range = parseBedRange(prop);
  if (!range) return [];
  const lines: BedroomAvailabilityLine[] = [];
  for (let bed = range.min; bed <= range.max; bed += 1) {
    lines.push({ label: shortBedLabel(bed), status: 'unknown', statusLabel: AVAILABILITY_STATUS_LABELS.unknown });
  }
  return lines;
}

/** Continental U.S. + nearby portfolio footprint (excludes Null Island / overseas). */
const MAP_LAT_MIN = 24;
const MAP_LAT_MAX = 50;
const MAP_LNG_MIN = -125;
const MAP_LNG_MAX = -65;

export function getLatLng(prop: CommunityProperty): { lat: number; lng: number } | null {
  const latRaw = prop.property_address_latitude;
  const lngRaw = prop.property_address_longitude;
  // Number(null) / Number('') === 0 → Null Island (Gulf of Guinea). Reject empties first.
  if (latRaw == null || lngRaw == null || latRaw === '' || lngRaw === '') return null;
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat < MAP_LAT_MIN || lat > MAP_LAT_MAX || lng < MAP_LNG_MIN || lng > MAP_LNG_MAX) {
    return null;
  }
  return { lat, lng };
}

export const FILTER_PRICE_BOUNDS = { min: 0, max: 5000 } as const;
export const FILTER_SQFT_BOUNDS = { min: 0, max: 2500 } as const;

function parseBedValue(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const lower = String(raw).toLowerCase().trim();
  if (lower === 'studio') return 0;
  const n = Number(lower);
  return Number.isNaN(n) ? null : n;
}

function parseBathValue(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(String(raw).trim());
  return Number.isNaN(n) ? null : n;
}

/** Parse `property_sqft_range` like "723 - 1015" or "684 - 1,547 sq ft". */
export function parseSqftRange(prop: CommunityProperty): { min: number; max: number } | null {
  const raw = String(prop.property_sqft_range || '').replace(/,/g, '');
  const match = raw.match(/(\d+)\s*-\s*(\d+)/);
  if (!match) return null;
  const min = Number(match[1]);
  const max = Number(match[2]);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;
  return { min, max };
}

export function parseBedRange(prop: CommunityProperty): { min: number; max: number } | null {
  const min = parseBedValue(prop.property_bedrooms_min);
  const max = parseBedValue(prop.property_bedrooms_max ?? prop.property_bedrooms_min);
  if (min == null) return null;
  return { min, max: max == null ? min : max };
}

export function parseBathRange(prop: CommunityProperty): { min: number; max: number } | null {
  const min = parseBathValue(prop.property_bathrooms_min);
  const max = parseBathValue(prop.property_bathrooms_max ?? prop.property_bathrooms_min);
  if (min == null) return null;
  return { min, max: max == null ? min : max };
}

export const COMMUNITY_LIFESTYLE_OPTIONS = [
  { id: 'pet-friendly', label: 'Pet friendly' },
  { id: 'available-now', label: 'Available now' },
  { id: 'specials', label: 'Specials' },
  { id: 'pool', label: 'Pool' },
  { id: 'fitness-center', label: 'Fitness center' },
  { id: 'in-home-laundry', label: 'In-home laundry' },
  { id: 'covered-parking', label: 'Covered parking' },
] as const;

export type CommunityLifestyleFilter = (typeof COMMUNITY_LIFESTYLE_OPTIONS)[number]['id'];

export interface CommunityListFilters {
  priceMin: number;
  priceMax: number;
  sqftMin: number;
  sqftMax: number;
  bedrooms: number[];
  bathrooms: number[];
  states: string[];
  cities: string[];
  lifestyle: CommunityLifestyleFilter[];
}

export function defaultCommunityListFilters(seedState?: string): CommunityListFilters {
  return {
    priceMin: FILTER_PRICE_BOUNDS.min,
    priceMax: FILTER_PRICE_BOUNDS.max,
    sqftMin: FILTER_SQFT_BOUNDS.min,
    sqftMax: FILTER_SQFT_BOUNDS.max,
    bedrooms: [],
    bathrooms: [],
    states: seedState ? [seedState] : [],
    cities: [],
    lifestyle: [],
  };
}

export function formatFromPrice(prop: CommunityProperty): string | null {
  const priceMin = Number(prop.property_price_min);
  if (Number.isNaN(priceMin) || priceMin <= 0) return null;
  return `from ${formatCurrency(priceMin)}`;
}

export function formatBedsSummary(prop: CommunityProperty): string | null {
  const beds = parseBedRange(prop);
  if (!beds) return null;
  const label = (n: number) => (n === 0 ? 'Studio' : `${n} Bed`);
  if (beds.min === beds.max) return label(beds.min);
  return `${label(beds.min)} – ${label(beds.max)}`;
}

export function haversineMiles(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(h));
}

export type CommunityCityOption = {
  city: string;
  slug: string;
  state: string;
  count: number;
};

export function formatCityWithState(city: string, state?: string): string {
  const abbr = normalizeAbbr(state || '');
  return abbr ? `${city}, ${abbr}` : city;
}

export function getTopMetroCities(
  properties: CommunityProperty[],
  limit = 8,
): CommunityCityOption[] {
  const counts = new Map<string, CommunityCityOption>();
  for (const prop of properties) {
    const city = String(prop.property_address_city || '').trim();
    const slug = String(prop._citySlug || slugify(city)).toLowerCase();
    const state = normalizeAbbr(
      String(prop.property_address_state || prop._stateSlug || ''),
    );
    if (!city || !slug) continue;
    const existing = counts.get(slug);
    if (existing) existing.count += 1;
    else counts.set(slug, { city, slug, state, count: 1 });
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.city.localeCompare(b.city)).slice(0, limit);
}

/** All cities with properties, alphabetically — for filter dropdowns. */
export function getAllCityOptions(properties: CommunityProperty[]): CommunityCityOption[] {
  return getTopMetroCities(properties, Number.MAX_SAFE_INTEGER).sort((a, b) =>
    a.city.localeCompare(b.city),
  );
}

export type CommunityAmenityFlag = 'pet' | 'pool' | 'fitness' | 'laundry' | 'parking' | 'specials' | 'available';

export function getCommunityAmenityFlags(prop: CommunityProperty): CommunityAmenityFlag[] {
  const enrichment = getSearchEnrichment(prop);
  const text = enrichment?.text || '';
  const flags: CommunityAmenityFlag[] = [];
  if (enrichment?.pet || enrichment?.dogs || enrichment?.cats) flags.push('pet');
  if (enrichment?.availableNow || Number(prop.property_available_units) > 0) flags.push('available');
  if (
    enrichment?.specials ||
    prop.property_specialoffer_title ||
    (Array.isArray(prop.special_offers) && prop.special_offers.some((o: { title?: string }) => o?.title))
  ) {
    flags.push('specials');
  }
  if (/\bpool\b/.test(text)) flags.push('pool');
  if (/\bfitness\b|\bgym\b/.test(text)) flags.push('fitness');
  if (/\bw\s*&\s*d\b|\bwasher\b|\bin[-\s]?home\s+(?:washer|laundry)\b|\bin[-\s]?unit\s+laundry\b/.test(text)) {
    flags.push('laundry');
  }
  if (/covered\s+parking/.test(text)) flags.push('parking');
  return flags;
}

export function parseListFiltersFromSearchParams(params: URLSearchParams): CommunityListFilters {
  const base = defaultCommunityListFilters();
  const num = (key: string, fallback: number) => {
    const raw = params.get(key);
    if (raw == null || raw === '') return fallback;
    const value = Number(raw);
    return Number.isNaN(value) ? fallback : value;
  };
  const list = (key: string) =>
    (params.get(key) || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const bedrooms = list('beds')
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  const bathrooms = list('baths')
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  const lifestyle = list('lifestyle').filter((id): id is CommunityLifestyleFilter =>
    COMMUNITY_LIFESTYLE_OPTIONS.some((option) => option.id === id),
  );

  return {
    priceMin: Math.max(FILTER_PRICE_BOUNDS.min, Math.min(FILTER_PRICE_BOUNDS.max, num('priceMin', base.priceMin))),
    priceMax: Math.max(FILTER_PRICE_BOUNDS.min, Math.min(FILTER_PRICE_BOUNDS.max, num('priceMax', base.priceMax))),
    sqftMin: Math.max(FILTER_SQFT_BOUNDS.min, Math.min(FILTER_SQFT_BOUNDS.max, num('sqftMin', base.sqftMin))),
    sqftMax: Math.max(FILTER_SQFT_BOUNDS.min, Math.min(FILTER_SQFT_BOUNDS.max, num('sqftMax', base.sqftMax))),
    bedrooms,
    bathrooms,
    states: list('state').map((s) => s.toLowerCase()),
    cities: list('city').map((c) => c.toLowerCase()),
    lifestyle,
  };
}

export function writeListFiltersToSearchParams(
  params: URLSearchParams,
  filters: CommunityListFilters,
  extras?: { search?: string; sort?: string },
) {
  const setOrDelete = (key: string, value: string | null | undefined) => {
    if (!value) params.delete(key);
    else params.set(key, value);
  };

  setOrDelete(
    'priceMin',
    filters.priceMin > FILTER_PRICE_BOUNDS.min ? String(filters.priceMin) : null,
  );
  setOrDelete(
    'priceMax',
    filters.priceMax < FILTER_PRICE_BOUNDS.max ? String(filters.priceMax) : null,
  );
  setOrDelete(
    'sqftMin',
    filters.sqftMin > FILTER_SQFT_BOUNDS.min ? String(filters.sqftMin) : null,
  );
  setOrDelete(
    'sqftMax',
    filters.sqftMax < FILTER_SQFT_BOUNDS.max ? String(filters.sqftMax) : null,
  );
  setOrDelete('beds', filters.bedrooms.length ? filters.bedrooms.join(',') : null);
  setOrDelete('baths', filters.bathrooms.length ? filters.bathrooms.join(',') : null);
  setOrDelete('state', filters.states.length ? filters.states.join(',') : null);
  setOrDelete('city', filters.cities.length ? filters.cities.join(',') : null);
  setOrDelete('lifestyle', filters.lifestyle.length ? filters.lifestyle.join(',') : null);
  setOrDelete('search', extras?.search || null);
  setOrDelete('sort', extras?.sort && extras.sort !== 'featured' ? extras.sort : null);
}

function propertyMatchesLifestyleFilter(
  prop: CommunityProperty,
  filter: CommunityLifestyleFilter,
): boolean {
  const enrichment = getSearchEnrichment(prop);
  const text = enrichment?.text || '';
  const haystack = propertySearchHaystack(prop, enrichment);

  switch (filter) {
    case 'pet-friendly':
      return Boolean(
        enrichment?.pet ||
          enrichment?.dogs ||
          enrichment?.cats ||
          prop.property_ispetfriendly === true ||
          prop.property_ispetfriendly === 'true' ||
          /\bpet/.test(haystack),
      );
    case 'available-now':
      if (enrichment?.availableNow != null) return enrichment.availableNow;
      return Number(prop.property_available_units) > 0;
    case 'specials':
      if (enrichment?.specials != null) return enrichment.specials;
      return Boolean(
        prop.property_specialoffer_title ||
          (Array.isArray(prop.special_offers) && prop.special_offers.some((o: { title?: string }) => o?.title)),
      );
    case 'pool':
      return /\bpool\b/.test(text) || /\bpool\b/.test(haystack);
    case 'fitness-center':
      return /\bfitness\b|\bgym\b/.test(text) || /\bfitness\b|\bgym\b/.test(haystack);
    case 'in-home-laundry':
      return (
        /\bw\s*&\s*d(?:\s+included)?\b|\bwasher\b|\bin[-\s]?home\s+(?:washer|laundry)\b|\bin[-\s]?unit\s+laundry\b/.test(
          text,
        ) ||
        /\bw\s*&\s*d(?:\s+included)?\b|\bwasher\b|\bin[-\s]?home\s+(?:washer|laundry)\b|\bin[-\s]?unit\s+laundry\b/.test(
          haystack,
        )
      );
    case 'covered-parking':
      return /covered\s+parking/.test(text) || /covered\s+parking/.test(haystack);
    default:
      return true;
  }
}

function rangesOverlap(
  aMin: number,
  aMax: number,
  bMin: number,
  bMax: number,
): boolean {
  return aMin <= bMax && aMax >= bMin;
}

export function propertyMatchesListFilters(
  prop: CommunityProperty,
  filters: CommunityListFilters,
): boolean {
  if (filters.states.length > 0) {
    const slug = (prop._stateSlug || '').toLowerCase();
    if (!filters.states.includes(slug)) return false;
  }

  if (filters.cities?.length > 0) {
    const slug = (prop._citySlug || '').toLowerCase();
    if (!filters.cities.includes(slug)) return false;
  }

  const priceMin = Number(prop.property_price_min);
  const priceMax = Number(prop.property_price_max ?? prop.property_price_min);
  if (!Number.isNaN(priceMin)) {
    const pMax = Number.isNaN(priceMax) ? priceMin : priceMax;
    if (!rangesOverlap(priceMin, pMax, filters.priceMin, filters.priceMax)) return false;
  }

  const sqft = parseSqftRange(prop);
  if (sqft && !rangesOverlap(sqft.min, sqft.max, filters.sqftMin, filters.sqftMax)) {
    return false;
  }

  if (filters.bedrooms.length > 0) {
    const beds = parseBedRange(prop);
    if (!beds) return false;
    const matches = filters.bedrooms.some((bed) => bed >= beds.min && bed <= beds.max);
    if (!matches) return false;
  }

  if (filters.bathrooms.length > 0) {
    const baths = parseBathRange(prop);
    if (!baths) return false;
    const matches = filters.bathrooms.some((bath) => bath >= baths.min && bath <= baths.max);
    if (!matches) return false;
  }

  if (filters.lifestyle?.length > 0) {
    const matchesAll = filters.lifestyle.every((item) => propertyMatchesLifestyleFilter(prop, item));
    if (!matchesAll) return false;
  }

  return true;
}

export function getAvailableStates(properties: CommunityProperty[]): { slug: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const prop of properties) {
    const slug = (prop._stateSlug || '').toLowerCase();
    if (!slug || seen.has(slug)) continue;
    const abbr = normalizeAbbr(prop.property_address_state || slug);
    seen.set(slug, getStateName(abbr));
  }
  return [...seen.entries()]
    .map(([slug, name]) => ({ slug, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getSearchEnrichment(prop: CommunityProperty): CommunitySearchIndexEntry | undefined {
  const id = String(prop.multihub_property_id || '');
  if (id && communitySearchIndexById.has(id)) return communitySearchIndexById.get(id);
  const slug = String(prop._slug || prop.property_slug || '');
  if (slug && communitySearchIndexBySlug.has(slug)) return communitySearchIndexBySlug.get(slug);
  return undefined;
}

/** Stopwords / filler dropped when parsing free-form community search. */
const SEARCH_STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'of', 'for', 'to', 'in', 'on', 'at', 'near', 'around',
  'with', 'that', 'this', 'my', 'our', 'me', 'i', 'looking', 'look', 'find', 'show',
  'want', 'need', 'please', 'apartment', 'apartments', 'apt', 'apts', 'home', 'homes',
  'community', 'communities', 'housing', 'unit', 'units', 'place', 'places', 'rent',
  'rental', 'rentals', 'lease', 'leasing', 'living', 'by', 'from', 'per', 'month', 'mo',
  'bed', 'beds', 'bedroom', 'bedrooms', 'bath', 'baths', 'bathroom', 'bathrooms',
  'friendly', 'area', 'city',
]);

const NUMBER_WORDS: Record<string, string> = { one: '1', two: '2', three: '3', four: '4' };

/** Lowercase and unify punctuation/abbreviations so "St. Pete", "Saint" and "Ft" compare equal. */
function normalizeSearchText(value: string | null | undefined): string {
  return String(value || '')
    .toLowerCase()
    .replace(/(?<!\d)\.|\.(?!\d)/g, '')
    .replace(/\bsaint\b/g, 'st')
    .replace(/\bft\b/g, 'fort')
    .replace(/\s+/g, ' ')
    .trim();
}

const AMENITY_ALIASES: { pattern: RegExp; terms: string[] }[] = [
  { pattern: /\bpet[-\s]?friendly\b|\bpets?\s+allowed\b|\ballow(?:s|ed)?\s+pets?\b|\bpets?\b/i, terms: ['pet'] },
  { pattern: /\bdog[-\s]?friendly\b|\bdogs?\b/i, terms: ['dog'] },
  { pattern: /\bcat[-\s]?friendly\b|\bcats?\b/i, terms: ['cat'] },
  { pattern: /\bpool\b|\bswimming\b/i, terms: ['pool', 'swim'] },
  { pattern: /\bfitness\b|\bgym\b|\bworkout\b/i, terms: ['fitness', 'gym'] },
  { pattern: /\bparking\b|\bgarage\b/i, terms: ['parking', 'garage'] },
  { pattern: /\bwash(?:er)?\s*(?:and|&|\/)?\s*dry(?:er)?\b|\bw\s*&\s*d\b|\bin[-\s]?unit\s+laundry\b/i, terms: ['washer', 'dryer', 'w&d', 'laundry'] },
  { pattern: /\bbalcony\b|\bpatio\b/i, terms: ['balcony', 'patio'] },
  { pattern: /\bev\b|\belectric\s+vehicle\b|\bcharging\b/i, terms: ['ev', 'charging'] },
  { pattern: /\bco[-\s]?work(?:ing)?\b|\bbusiness\s+center\b/i, terms: ['co-work', 'cowork', 'business'] },
];

export interface ParsedCommunitySearch {
  raw: string;
  cities: string[];
  states: string[];
  zip: string | null;
  bedrooms: number[];
  bathrooms: number[];
  priceMax: number | null;
  petFriendly: boolean;
  dogs: boolean;
  cats: boolean;
  amenityTerms: string[];
  freeText: string[];
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const key = value.toLowerCase().trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * Parse natural-language community queries like
 * "pet friendly apartments in tampa" or "2 bedroom under $1800 in austin tx".
 */
export function parseCommunitySearchQuery(
  query: string,
  knownCities: string[] = [],
  knownStates: { slug: string; name: string }[] = [],
): ParsedCommunitySearch {
  const raw = (query || '').trim();
  let working = normalizeSearchText(raw).replace(
    /\b(one|two|three|four)(?=\s*-?\s*(?:bed|bd|br))/g,
    (word) => NUMBER_WORDS[word],
  );

  const cities: string[] = [];
  const states: string[] = [];
  const bedrooms: number[] = [];
  const bathrooms: number[] = [];
  const amenityTerms: string[] = [];
  let zip: string | null = null;
  let priceMax: number | null = null;
  let petFriendly = false;
  let dogs = false;
  let cats = false;

  const cityCandidates = [...knownCities]
    .map((c) => normalizeSearchText(c))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const city of cityCandidates) {
    const needle = city;
    const re = new RegExp(`\\b${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(working)) {
      cities.push(needle);
      working = working.replace(re, ' ');
    }
  }

  for (const state of knownStates) {
    const nameRe = new RegExp(`\\b${state.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    const slugRe = new RegExp(`\\b${state.slug}\\b`, 'i');
    if (nameRe.test(working) || slugRe.test(working)) {
      states.push(state.slug.toLowerCase());
      working = working.replace(nameRe, ' ').replace(slugRe, ' ');
    }
  }

  const zipMatch = working.match(/\b(\d{5})(?:-\d{4})?\b/);
  if (zipMatch) {
    zip = zipMatch[1];
    working = working.replace(zipMatch[0], ' ');
  }

  const priceMatch =
    working.match(/(?:under|below|less than|max(?:imum)?|up to)\s*\$?\s*([\d,.]+)\s*(k\b)?/i) ||
    working.match(/\$\s*([\d,.]+)\s*(k\b)?\s*(?:or less|max)?/i);
  if (priceMatch) {
    const n = Number(priceMatch[1].replace(/,/g, '')) * (priceMatch[2] ? 1000 : 1);
    if (!Number.isNaN(n)) priceMax = n;
    working = working.replace(priceMatch[0], ' ');
  }

  const studioRe = /\bstudios?\b/i;
  if (studioRe.test(working)) {
    bedrooms.push(0);
    working = working.replace(/\bstudios?\b/gi, ' ');
  }

  const bedRe = /\b(\d+)\s*-?\s*(?:bed(?:room)?s?|bdrms?|bds?|br)\b/gi;
  let bedMatch: RegExpExecArray | null;
  while ((bedMatch = bedRe.exec(working))) {
    bedrooms.push(Number(bedMatch[1]));
  }
  working = working.replace(bedRe, ' ');

  const bathRe = /\b(\d+(?:\.\d+)?)\s*-?\s*(?:bath(?:room)?s?|ba)\b/gi;
  let bathMatch: RegExpExecArray | null;
  while ((bathMatch = bathRe.exec(working))) {
    bathrooms.push(Math.floor(Number(bathMatch[1])));
  }
  working = working.replace(bathRe, ' ');

  for (const alias of AMENITY_ALIASES) {
    if (!alias.pattern.test(raw) && !alias.pattern.test(working)) continue;
    if (alias.terms.includes('pet')) petFriendly = true;
    if (alias.terms.includes('dog')) {
      dogs = true;
      petFriendly = true;
    }
    if (alias.terms.includes('cat')) {
      cats = true;
      petFriendly = true;
    }
    amenityTerms.push(...alias.terms);
    working = working.replace(alias.pattern, ' ');
  }

  const freeText = working
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !SEARCH_STOPWORDS.has(t));

  return {
    raw,
    cities: uniqueStrings(cities),
    states: uniqueStrings(states),
    zip,
    bedrooms: [...new Set(bedrooms)],
    bathrooms: [...new Set(bathrooms)],
    priceMax,
    petFriendly,
    dogs,
    cats,
    amenityTerms: uniqueStrings(amenityTerms.filter((t) => !['pet', 'dog', 'cat'].includes(t))),
    freeText: uniqueStrings(freeText),
  };
}

function propertySearchHaystack(prop: CommunityProperty, enrichment?: CommunitySearchIndexEntry): string {
  return [
    prop.property_name,
    prop.property_address_city,
    prop.property_address_state,
    prop.property_address_zip,
    prop.property_address,
    prop.property_full_address,
    prop.property_neighborhood_address,
    plainText(prop.property_short_description),
    enrichment?.text,
    enrichment?.pet ? 'pet friendly pets' : '',
    enrichment?.dogs ? 'dogs dog friendly' : '',
    enrichment?.cats ? 'cats cat friendly' : '',
  ]
    .filter(Boolean)
    .map((part) => normalizeSearchText(part))
    .join(' ');
}

export function propertyMatchesParsedSearch(
  prop: CommunityProperty,
  parsed: ParsedCommunitySearch,
): boolean {
  if (!parsed.raw) return true;

  const enrichment = getSearchEnrichment(prop);
  const city = normalizeSearchText(prop.property_address_city);
  const stateSlug = String(prop._stateSlug || '').toLowerCase();
  const zip = String(prop.property_address_zip || '');
  const haystack = propertySearchHaystack(prop, enrichment);

  if (parsed.cities.length > 0) {
    const cityOk = parsed.cities.some(
      (c) => city === c || city.includes(c) || haystack.includes(c),
    );
    if (!cityOk) return false;
  }

  if (parsed.states.length > 0 && !parsed.states.includes(stateSlug)) return false;

  if (parsed.zip && !zip.startsWith(parsed.zip)) return false;

  if (parsed.priceMax != null) {
    const priceMin = Number(prop.property_price_min);
    if (!Number.isNaN(priceMin) && priceMin > parsed.priceMax) return false;
  }

  if (parsed.bedrooms.length > 0) {
    const beds = parseBedRange(prop);
    if (!beds) return false;
    const matches = parsed.bedrooms.some((bed) => bed >= beds.min && bed <= beds.max);
    if (!matches) return false;
  }

  if (parsed.bathrooms.length > 0) {
    const baths = parseBathRange(prop);
    if (!baths) return false;
    const matches = parsed.bathrooms.some((bath) => bath >= baths.min && bath <= baths.max);
    if (!matches) return false;
  }

  if (parsed.petFriendly) {
    const ok =
      enrichment?.pet ||
      enrichment?.dogs ||
      enrichment?.cats ||
      /\bpet/.test(haystack);
    if (!ok) return false;
  }
  if (parsed.dogs && !(enrichment?.dogs || /\bdogs?\b/.test(haystack))) return false;
  if (parsed.cats && !(enrichment?.cats || /\bcats?\b/.test(haystack))) return false;

  if (parsed.amenityTerms.length > 0) {
    const amenityOk = parsed.amenityTerms.some((term) => haystack.includes(term));
    if (!amenityOk) return false;
  }

  if (parsed.freeText.length > 0) {
    const freeOk = parsed.freeText.every((term) => haystack.includes(term));
    if (!freeOk) return false;
  }

  // Structured-only queries (city/pet/beds with no leftover tokens) already matched above.
  // Exact substring fallback when the parser could not extract structure.
  const hasStructure =
    parsed.cities.length > 0 ||
    parsed.states.length > 0 ||
    parsed.zip ||
    parsed.bedrooms.length > 0 ||
    parsed.bathrooms.length > 0 ||
    parsed.priceMax != null ||
    parsed.petFriendly ||
    parsed.dogs ||
    parsed.cats ||
    parsed.amenityTerms.length > 0 ||
    parsed.freeText.length > 0;

  if (!hasStructure) {
    const normalized = normalizeSearchText(parsed.raw);
    const onlyFiller = normalized
      .split(/[^a-z0-9-]+/)
      .every((term) => term.length <= 1 || SEARCH_STOPWORDS.has(term));
    return onlyFiller || haystack.includes(normalized);
  }

  return true;
}

export function getAvailableCities(properties: CommunityProperty[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const prop of properties) {
    const city = String(prop.property_address_city || '').trim();
    if (!city) continue;
    const key = city.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(city);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

export { propertySummaries };
